import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { fetchAuthMe } from "../../redux/slices/auth";
import axios from "../../axios";
import { colors, font, radius, shadow } from "../../styles/theme/tokens";
import { slideKey } from "./keys";
import { NoteText } from "./common";

// Канал між вкладкою уроку і вікном ключів (той самий браузер учителя)
export const keyChannel = (lessonId) => `speaksy-key-${lessonId}`;

// Ключі для вчителя в окремому вікні: на уроці вчитель показує в Meet лише вкладку з уроком,
// а це вікно тримає поруч — учень його не бачить. Вікно саме йде за слайдами уроку.
function KeyPage() {
  const { lessonId } = useParams();
  const dispatch = useDispatch();
  const { data: me } = useSelector((state) => state.auth);
  const [lesson, setLesson] = useState(null);
  const [error, setError] = useState(null);
  const [index, setIndex] = useState(0);
  const [linked, setLinked] = useState(false);
  const currentRef = useRef(null);
  const isTeacher = ["teacher", "admin", "manager"].includes(me?.userType);

  useEffect(() => {
    if (!me || typeof me !== "object" || !me.userType) dispatch(fetchAuthMe());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    axios
      .get(`platform-lessons/${lessonId}`)
      .then((res) => setLesson(res.data))
      .catch(() => setError("Урок не знайдено або немає доступу"));
  }, [lessonId]);

  useEffect(() => {
    document.title = "Ключі · Speaksy";
    if (typeof BroadcastChannel === "undefined") return undefined;
    const channel = new BroadcastChannel(keyChannel(lessonId));
    channel.onmessage = (event) => {
      if (typeof event.data?.index === "number") {
        setIndex(event.data.index);
        setLinked(true);
      }
    };
    channel.postMessage({ hello: true }); // урок відповідає поточним слайдом
    return () => channel.close();
  }, [lessonId]);

  useEffect(() => {
    currentRef.current?.scrollIntoView({ block: "start" }); // без анімації: у фоновому вікні плавна прокрутка зупиняється
  }, [index, lesson]);

  if (me?.userType && !isTeacher) return <Message>Ключі доступні лише вчителю.</Message>;
  if (error) return <Message>{error}</Message>;
  if (!lesson) return <Message>Завантажуємо ключі…</Message>;

  return (
    <Page>
      <Head>
        <b>Ключі · {lesson.number}. {lesson.title}</b>
        <span>{linked ? `Слайд ${index + 1} / ${lesson.slides.length} — іде за уроком` : "Відкрийте урок у сусідній вкладці — ключі підлаштуються під слайд"}</span>
      </Head>
      <List>
        {lesson.slides.map((slide, i) => {
          const key = slideKey(slide);
          const current = i === index;
          if (!current && !key.length && !slide.teacherNote) return null;
          return (
            <Card key={slide.id} ref={current ? currentRef : null} $current={current} onClick={() => setIndex(i)}>
              <CardTitle $current={current}>
                <em>{i + 1}</em> {slide.title} {slide.section === "Homework" && <Tag>ДЗ</Tag>}
              </CardTitle>
              {key.length > 0 && (
                <Answers>
                  {key.map((row, k) => (
                    <li key={k}>
                      {row.n !== undefined && <i>{row.n}</i>}
                      {row.text}
                    </li>
                  ))}
                </Answers>
              )}
              {slide.teacherNote && <Note><NoteText text={slide.teacherNote} /></Note>}
              {current && !key.length && !slide.teacherNote && <Note>На цьому слайді немає ключів (розмова / правило).</Note>}
            </Card>
          );
        })}
      </List>
    </Page>
  );
}

const Page = styled.div`
  min-height: 100vh;
  background: ${colors.bg};
  font-family: ${font.text};
  color: ${colors.ink};
`;

const Head = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 18px;
  background: ${colors.brandDark};
  color: #fff;
  font-size: 15px;

  span {
    font-size: 13px;
    opacity: 0.8;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 16px 60vh;
`;

const Card = styled.div`
  scroll-margin-top: 80px;
  padding: 14px 16px;
  border-radius: ${radius.lg};
  background: ${colors.surface};
  box-shadow: ${shadow.card};
  border: 3px solid ${(props) => (props.$current ? colors.brand : "transparent")};
  opacity: ${(props) => (props.$current ? 1 : 0.6)};
  cursor: pointer;
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: ${(props) => (props.$current ? "18px" : "15px")};
  font-weight: 800;

  em {
    display: inline-grid;
    place-items: center;
    min-width: 28px;
    height: 28px;
    border-radius: 50%;
    font-style: normal;
    font-size: 13px;
    color: #fff;
    background: ${colors.brand};
  }
`;

const Tag = styled.span`
  padding: 2px 8px;
  border-radius: ${radius.pill};
  font-size: 12px;
  color: ${colors.success};
  background: ${colors.successSoft};
`;

const Answers = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 17px;
  font-weight: 700;
  color: ${colors.success};

  li {
    display: flex;
    gap: 8px;
  }

  i {
    min-width: 20px;
    font-style: normal;
    color: ${colors.muted};
  }
`;

const Note = styled.div`
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: ${radius.md};
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-line;
  color: ${colors.ink};
  background: ${colors.amberSoft};
`;

const Message = styled.div`
  display: grid;
  place-items: center;
  min-height: 100vh;
  font-family: ${font.text};
  font-size: 18px;
  color: ${colors.muted};
`;

export default KeyPage;
